import { getDisplayAssetCode } from '@altify/utils';
import { useGetProductDepositInfo } from 'store/slices/cdn-api';
import {
  useGetHoldingsQuery,
  useGetProductHolding,
} from 'store/slices/holdings-api';
import { useGetAvailableProductsQuery } from 'store/slices/products-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import { useGetRate, useGetRatesQuery } from 'store/slices/rates-api';

export function useProducts() {
  const { data: availableProducts, isLoading } = useGetAvailableProductsQuery();

  if (availableProducts) {
    const mappedProducts = availableProducts.map(product => ({
      ...product,
      isTracked: product.isTracked ?? true, // Default to true if not specified
      displayCode: getDisplayAssetCode(product.code),
    }));

    return { products: mappedProducts, isLoading };
  }

  return { products: null, isLoading };
}

export function useProduct(productCode?: string) {
  // Products always change in one API call so no need to optimize here
  const { products, isLoading } = useProducts();
  if (!productCode) {
    return { product: null, isLoading };
  }

  const product = products?.find(
    product => product.code.toUpperCase() === productCode.toUpperCase(),
  );

  return { product, isLoading };
}

export function useFunctionalCurrency() {
  const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery();
  const {
    product: functionalCurrency,
    isLoading: isFunctionalCurrencyLoading,
  } = useProduct(profile?.baseCurrency?.code);

  return {
    functionalCurrency,
    isLoading: isProfileLoading || isFunctionalCurrencyLoading,
  };
}

export function useFunctionalCurrencyWithHoldings() {
  const { functionalCurrency, isLoading: isFunctionalCurrencyLoading } =
    useFunctionalCurrency();
  const {
    product: functionalCurrencyProduct,
    isLoading: isFunctionalCurrencyProductLoading,
  } = useProductWithHoldings(functionalCurrency?.code);
  const isLoading =
    isFunctionalCurrencyLoading || isFunctionalCurrencyProductLoading;

  return { functionalCurrency: functionalCurrencyProduct, isLoading };
}

export function useProductsWithHoldings() {
  const { products, isLoading: isProductsLoading } = useProducts();
  const { data: holdings, isLoading: isHoldingsLoading } =
    useGetHoldingsQuery();
  const isLoading = isProductsLoading || isHoldingsLoading;

  if (products && holdings) {
    const productsWithHoldings = products.map(product => {
      const holding = holdings.entities[product.code];
      return {
        ...product,
        totalAmount: holding ? holding.totalAmount : 0,
        reservedAmount: holding ? holding.reservedAmount : 0,
        availableAmount: holding
          ? holding.totalAmount - holding.reservedAmount
          : 0,
        holdingsLastChangedAt:
          holding != null ? new Date(holding.lastChangedAt) : undefined,
        wac: holding ? holding.wac : undefined,
      };
    });

    const lastChangedAt = new Date(
      productsWithHoldings.reduce((acc, product) => {
        return Math.max(acc, product.holdingsLastChangedAt?.getTime() ?? 0);
      }, 0),
    );

    return { products: productsWithHoldings, lastChangedAt, isLoading };
  }

  return { products: null, isLoading };
}

export function useProductWithHoldings(productCode?: string) {
  const { product, isLoading: isProductLoading } = useProduct(productCode);
  const { data: holdings, isLoading: isHoldingsLoading } =
    useGetProductHolding(productCode);
  const isLoading = isProductLoading || isHoldingsLoading;

  if (!productCode) {
    return { product: null, isLoading };
  }

  if (product && !isHoldingsLoading) {
    const productWithHoldings = {
      ...product,
      totalAmount: holdings?.totalAmount ?? 0,
      reservedAmount: holdings?.reservedAmount ?? 0,
      availableAmount:
        holdings != null ? holdings.totalAmount - holdings.reservedAmount : 0,
      holdingsLastChangedAt:
        holdings != null ? new Date(holdings?.lastChangedAt) : undefined,
      wac: holdings?.wac,
    };

    return { product: productWithHoldings, isLoading };
  }

  return { product: null, isLoading };
}

export function useProductsWithHoldingsAndRates(displayCurrency?: string) {
  const {
    products,
    lastChangedAt: holdingsLastChangedAt,
    isLoading: isProductsLoading,
  } = useProductsWithHoldings();
  const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery();
  const { currentData: rates, isLoading: isRatesLoading } = useGetRatesQuery({
    displayCurrency,
  });
  const isLoading = isProductsLoading || isRatesLoading || isProfileLoading;

  if (products && rates && profile) {
    const displayProductCode = displayCurrency ?? profile.displayCurrency?.code;
    const ratesEntities = rates.entities;
    const productsWithFiatHoldings = products.map(product => {
      // Not tracked products don't have a rate
      if (!product.isTracked) {
        return {
          ...product,
          isTracked: false as const,
          rate: undefined,
          rateLastChangedAt: undefined,
          totalAmountInDisplay: undefined,
          reservedAmountInDisplay: undefined,
          availableAmountInDisplay: undefined,
          unrealizedGainLossAmount: undefined,
          unrealizedGainLossPercentage: undefined,
        };
      }

      const market = `${product.code}_${displayProductCode}`;
      const rate =
        product.code === displayProductCode ? 1 : ratesEntities[market]?.price;
      if (rate == null) {
        throw new Error(
          `Rate not found for product ${product.code} and display currency ${displayProductCode}`,
        );
      }

      return {
        ...product,
        isTracked: true as const,
        rate: rate,
        rateLastChangedAt: ratesEntities[market]?.timestamp,
        totalAmountInDisplay: rate * product.totalAmount,
        reservedAmountInDisplay: rate * product.reservedAmount,
        availableAmountInDisplay: rate * product.availableAmount,
        unrealizedGainLossAmount:
          (rate - (product.wac ?? 0)) * product.totalAmount,
        unrealizedGainLossPercentage:
          product.wac == null || product.wac == 0
            ? 0
            : (rate / product.wac - 1) * 100,
      };
    });

    const ratesLastChangedAt = new Date(
      productsWithFiatHoldings.reduce(
        (acc, product) =>
          product.isTracked
            ? Math.max(acc, new Date(product.rateLastChangedAt).getTime())
            : acc,
        0,
      ),
    );

    const totalPortfolioValue = productsWithFiatHoldings.reduce(
      (acc, product) => {
        return acc + (product.isTracked ? product.totalAmountInDisplay : 0);
      },
      0,
    );

    const productsWithFiatHoldingsAndBreakdown = productsWithFiatHoldings.map(
      product => ({
        ...product,
        breakdownPercentage: product.isTracked
          ? (product.totalAmountInDisplay / totalPortfolioValue) * 100
          : undefined,
      }),
    );

    const lastChangedAt = new Date(
      Math.max(ratesLastChangedAt.getTime(), holdingsLastChangedAt.getTime()),
    );
    return {
      products: productsWithFiatHoldingsAndBreakdown,
      totalPortfolioValue,
      isLoading,
      lastChangedAt,
    };
  }

  return { products: null, isLoading };
}

export function useProductWithHoldingsAndRates(
  productCode?: string,
  displayCurrency?: string,
) {
  const { product, isLoading: isProductLoading } =
    useProductWithHoldings(productCode);
  const { data: rate, isLoading: isRatesLoading } = useGetRate(
    productCode,
    displayCurrency,
  );
  const isLoading = isProductLoading || isRatesLoading;

  if (!productCode) {
    return { product: null, isLoading };
  }

  if (product && !product.isTracked) {
    throw new Error(
      `Rate not found for product ${product.code} and display currency ${displayCurrency}`,
    );
  }

  if (product && rate) {
    const productWithFiatHoldings = {
      ...product,
      rate: rate.price,
      rateLastChangedAt: rate.timestamp,
      totalAmountInDisplay: rate.price * product.totalAmount,
      reservedAmountInDisplay: rate.price * product.reservedAmount,
      availableAmountInDisplay: rate.price * product.availableAmount,
      unrealizedGainLossAmount:
        product.wac != null && product.wac != 0
          ? (rate.price - product.wac) * product.totalAmount
          : undefined,
      unrealizedGainLossPercentage:
        product.wac != null && product.wac != 0
          ? (rate.price / product.wac - 1) * 100
          : undefined,
    };

    return { product: productWithFiatHoldings, isLoading };
  }

  return { product: null, isLoading };
}

export function useProductWithDepositInfo(productCode: string) {
  const { product, isLoading: isProductLoading } = useProduct(productCode);
  const { data: depositInfo, isLoading: isDepositInfoLoading } =
    useGetProductDepositInfo(productCode);
  const isLoading = isProductLoading || isDepositInfoLoading;

  if (!productCode) {
    return { product: null, isLoading };
  }

  if (product && depositInfo) {
    const productWithDepositInfo = {
      ...product,
      network: depositInfo.network,
    };

    return { product: productWithDepositInfo, isLoading };
  }

  return { product: null, isLoading };
}

export type Product = NonNullable<ReturnType<typeof useProduct>['product']>;
type ProductWithHoldings = NonNullable<
  ReturnType<typeof useProductWithHoldings>['product']
>;
export type ProductsWithHoldingsAndRates = NonNullable<
  ReturnType<typeof useProductsWithHoldingsAndRates>['products']
>;
export type ProductWithHoldingsAndRates = NonNullable<
  ReturnType<typeof useProductWithHoldingsAndRates>['product']
>;
